// @flow
/**
 * Template for swipeable + mobile tab views
 * Use this component for tab views so you don't have to care about design
 *
 * Accepts props:
 *  tabs: array of Object = {label: string, icon: Icon material-ui element}
 *  content: array of react/jsx elements
 *
 * If no props are given, they default to empty arrays
 * The tabs and content arrays should be equally large, since you want to have a content in each tab
 */
import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Paper from "@material-ui/core/Paper";

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    width: "100%",
  },
});

type State = {
  value: number,
};

type Props = {
  classes: Object,
  tabs: Array<{
    id?: string,
    label: string,
    icon?: React$Element<*>,
    content: React$Element<*>,
    hidden?: boolean,
  }>,
  parentId?: string,
  onTabChange?: (newTab: number) => void,
};

class Tabz extends Component<Props, State> {
  state = {
    value: 0,
  };

  static defaultProps = {
    tabs: [],
  };

  componentDidMount() {
    const forcedId = this.parseHash();
    if (forcedId) {
      const index = this.getTabIndexById(forcedId);
      if (index !== -1) {
        this.setState({ value: index });
      }
    }
  }

  parseHash = (): string | null => {
    const { parentId } = this.props;
    const hash = window.location.hash;
    if (hash.includes("#")) {
      const segments = hash.split("#").slice(1);
      if (parentId && segments[0] === parentId) {
        return segments[1]; // Return the child tab ID if the parent matches
      } else if (!parentId) {
        return segments[0]; // Return the first segment for the top-level tab
      }
    }
    return null;
  };

  updateHash = (tabId: string) => {
    const { parentId } = this.props;
    const newHash = parentId ? `#${parentId}#${tabId}` : `#${tabId}`;
    window.history.replaceState(null, document.title, newHash);
  };

  getTabIndexById = (id: string): number => {
    const { tabs } = this.props;
    return tabs.findIndex((tab) => tab.id === id);
  };

  handleChange = (event: SyntheticEvent<>, newValue: number) => {
    const { tabs, onTabChange } = this.props;
    const tabId = tabs[newValue]?.id;

    if (tabId) {
      this.updateHash(tabId);
    }

    this.setState({ value: newValue });

    if (onTabChange) {
      onTabChange(newValue);
    }
  };


  render() {
    const { classes, tabs } = this.props;
    const { value } = this.state;

    const visibleTabs = tabs.filter((tab) => !tab.hidden);

    return (
      <Paper elevation={2}>
        <div className={classes.root}>
          <AppBar position="static" color="default">
            <Tabs
              value={value}
              onChange={this.handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="on"
            >
              {visibleTabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={tab.icon}
                  label={tab.label}
                  wrapped
                  data-test={tab.data_test}
                />
              ))}
            </Tabs>
          </AppBar>
          {visibleTabs[value]?.content}
        </div>
      </Paper>
    );
  }
}

export default withStyles(styles)(Tabz);
